import { FontFamily } from '@/model/fontManager/fontFamily/fontFamily';
import { D3TextLayer } from '@/model/document2/page/surface/design/layer/d3TextLayer';
import { RequestHelper } from './requestHelper';
import { ImageHelper } from './imageHelper';
import { MathHelper } from './mathHelper';
import { UnitHelper } from './unitHelper';
import { DebugHelper } from "@/helper/debugHelper";

// 3Dのレンダリング
export class D3Helper
{
    // このサイズを基準として作成する
    static readonly BASE_RENDERING_SIZE = 1200;

    // 3Dテキスト画像を取得 png base64
    public static getTextImage(layer : D3TextLayer, fontSize : number)
    {
        var THREE = require('three');
        THREE.Cache.enabled = true;

        // まずレイヤーサイズの x4 * x4 の大きさを基準にステージを決める
        // これならはみ出さないだろう
        var requireWidth = UnitHelper.convert(Math.abs(layer.width) * 4, "mm", "px");
        var requireHeight = UnitHelper.convert(Math.abs(layer.height) * 4, "mm", "px");
        // ところがA4などpxが大きくなりすぎるとレンダリングに異常が出るため、
        // 1200 x 1200以内に収まるサイズにリサイズを行う。
        var scale = this.BASE_RENDERING_SIZE / Math.max(requireWidth, requireHeight);
        // このスケールを適用してpxを調整する。

        // フォントサイズはこのサイズを基準にしているため、基準サイズにスケーリングする
        var fontSizePx = UnitHelper.convert(fontSize, "pt", "px");
        var textSize = fontSizePx * scale;

        // レンダラーを作成
        var width = requireWidth * scale;
        var height = requireHeight * scale;
        var renderer = new THREE.WebGLRenderer({antialias : true, alpha: true});
        renderer.setSize(width, height);

        // シーンを作成
        var scene = new THREE.Scene();

        // カメラを作成
        var camera = new THREE.PerspectiveCamera(75, width / height, 1, 10000);
        camera.position.set(0, 0, 250);

        // 環境光(全体を明るく)
        var ambientLight = new THREE.AmbientLight(0xffffff);
        // 強さ
        var ambient = (layer.brightness + 256) / 512;
        ambientLight.color.multiplyScalar(ambient);
        scene.add(ambientLight);

        // 点光源
        var pointLight = new THREE.PointLight(0xFFFFFF, 1, 1000);
        pointLight.position.set(layer.lightX, -layer.lightY, layer.lightZ);
        scene.add(pointLight);

        // フォントのパース
        var fontLoader = new THREE.FontLoader();
        var jsonFontObject = JSON.parse(layer.fontJson);
        D3Helper.fixFontIfNeeded(layer, jsonFontObject);
        var font = fontLoader.parse(jsonFontObject);
        
        var text = layer.text != "" ? layer.text : D3TextLayer.PLACEHOLDER;
        text = this.fixText(text);
        DebugHelper.trace("textsize : " + textSize);
        var height = textSize * layer.thickness / 100;
        var textGeometry = new THREE.TextGeometry(text, {
            font : font,
            size: textSize,
            height: height,
            curveSegments: 12,
        });
        textGeometry.computeBoundingBox();

        // 中央に移動する
        textGeometry.center();

        // 面マテリアル
        var specular = this.rgba2rgb(layer.textColor.r, layer.textColor.g, layer.textColor.b, (layer.reflect + 256) / 512);
        var material = new THREE.MeshPhongMaterial({
            color : new THREE.Color("rgb(" + layer.textColor.r + ", " + layer.textColor.g + ", " + layer.textColor.b + ")"),
            specular : new THREE.Color(specular),
            shininess : (layer.reflect + 256) / 512 * 200
        });

        var mesh = new THREE.Mesh(textGeometry, material);
        scene.add(mesh);

        // 回転角度
        var xRad = layer.rotateX * Math.PI / 180;
        var yRad = -layer.rotateY * Math.PI / 180;
        var zRad = layer.rotateZ * Math.PI / 180;
        mesh.rotation.set(xRad, yRad, zRad);

        // レンダリング
        renderer.render(scene, camera);

        // レンダリングしたキャンバスを画像化
        var image = renderer.domElement.toDataURL("image/png");
        
        return image;
    }

    // 一部文字が文字化けするので変換する
    private static fixText(text : string)
    {
        var fixed = text.replace(/～/g, "〜").replace(/\u3000/g, " ");
        
        return fixed;
    }

    // 一部フォントでglyphsの設定がおかしいため、文字が重なって表示されてしまうのを補正
    private static fixFontIfNeeded(layer : D3TextLayer, jsonFontObject : any)
    {
        try
        {
            if(layer.fontId == "noto-sans-jp-100" ||
                layer.fontId == "noto-sans-jp-300" ||
                layer.fontId == "noto-sans-jp-400" ||
                layer.fontId == "noto-sans-jp-500" ||
                layer.fontId == "noto-sans-jp-700" ||
                layer.fontId == "noto-sans-jp-900" ||
                layer.fontId == "noto-serif-jp-200" ||
                layer.fontId == "noto-serif-jp-300" ||
                layer.fontId == "noto-serif-jp-400" || 
                layer.fontId == "noto-serif-jp-500" ||
                layer.fontId == "noto-serif-jp-600" ||
                layer.fontId == "noto-serif-jp-700" || 
                layer.fontId == "noto-serif-jp-900")
            {
                var boundingBox = jsonFontObject.boundingBox;
                var glyphs = jsonFontObject.glyphs;
                for(var key in glyphs)
                {
                    glyphs[key].x_min = boundingBox.xMin;
                    glyphs[key].x_max = boundingBox.xMax;
                    glyphs[key].ha = boundingBox.xMin + boundingBox.xMax;
                }
            }
        }
        catch(ex)
        {
            DebugHelper.trace(ex);
        }
    }

    private static rgba2rgb(r, g, b, a)
    {
        var newR = Math.round((1 - a) * 255 + a * r);
        var newG = Math.round((1 - a) * 255 + a * g);
        var newB = Math.round((1 - a) * 255 + a * b);
        return "rgb(" + newR + ", " + newG + ", " + newB + ")"
    }

    // public static computeScreenSpaceBoundingBox(mesh, camera)
    // {
    //     var THREE = require('three');

    //     var vertices = mesh.geometry.vertices;
    //     DebugHelper.trace(vertices[0].x)
    //     var vertex = new THREE.Vector3();
    //     var min = new THREE.Vector3(1);
    //     var max = new THREE.Vector3(-1);

    //     for(var i = 0; i < vertices.length; ++i)
    //     {
    //         var vertexWorldCoord = vertex.copy(vertices[i]).applyMatrix4(mesh.matrixWorld);
    //         var vertexScreenSpace = vertexWorldCoord.project(camera);
    //         min.min(vertexScreenSpace);
    //         max.max(vertexScreenSpace);
    //     }

    //     return new THREE.Box2(min, max);
    // }

    // public static normalizedToPixels(coord, renderWidthPixels, renderHeightPixels) 
    // {
    //     var THREE = require('three');
 
    //     var halfScreen = new THREE.Vector2(renderWidthPixels / 2, renderHeightPixels / 2) 
    //     return coord.clone().multiply(halfScreen); 
    // } 
}